import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  LogOut,
  Settings,
  Users,
  Map,
  BotMessageSquare,
  ChevronDown,
  ChevronRight,
  Lock,
  FileText,
  Cloud,
  Package,
  Workflow,
  Plug,
  BarChart3,
  CreditCard,
  Activity,
  Siren,
  MessageSquare,
  ShipWheel,
  Sparkles,
  BookOpen,
  ShieldCheck,
  ExternalLink,
  Key,
  Send,
  PanelLeftClose,
  PanelLeftOpen,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { toast } from 'sonner';
import { SlackConnectResultModal, type SlackConnectResult } from './SlackConnectResultModal';
import { cn } from '@/lib/utils';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '@/components/theme-provider';
import { apiClient } from '../api/client';
import { ReactComponent as SlackIconDark } from '../assets/icons/slack-dark.svg';
import { isAdminOrOps } from '../utils/roles';
import { useSubscription } from '../hooks/useSubscription';

interface NavSubItem {
  name: string;
  href: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  customIcon?: { light: React.ComponentType<React.SVGProps<SVGSVGElement>>; dark: React.ComponentType<React.SVGProps<SVGSVGElement>> };
  imgIcon?: { light: string; dark: string };
  // Extra classes for the imgIcon (e.g. darker grayscale for light-toned brand marks).
  imgIconClass?: string;
  // Extra classes for mono/custom icons (e.g. size tweaks for padded viewBoxes).
  iconClass?: string;
  onPremOnly?: boolean;
  visibleTo?: 'admin_only' | 'admin_and_ops' | 'all';
}

interface NavItem {
  name: string;
  href?: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  visibleTo?: 'admin_only' | 'admin_and_ops' | 'all';
  hideOnPrem?: boolean;
  badge?: string;
  children?: NavSubItem[];
  productKey?: 'workflows' | 'incident_response' | 'copilot';
  // Legacy products (Incidents, Cloud AI Copilot) are hidden for every tenant
  // unless ops re-enables them via tenants.legacy_products_enabled.
  legacyProduct?: boolean;
}

const navigation: NavItem[] = [
  { name: 'Home', href: '/home', icon: Home },
  {
    name: 'Workflows',
    icon: Workflow,
    visibleTo: 'all',
    productKey: 'workflows',
    children: [
      { name: 'All Workflows', href: '/workflows', icon: Workflow, visibleTo: 'admin_and_ops' },
      { name: 'Executions', href: '/workflow-executions', icon: Activity, visibleTo: 'admin_and_ops' },
      { name: 'Library', href: '/workflows/suggestions', icon: BookOpen, visibleTo: 'admin_and_ops' },
      { name: 'Pending Approvals', href: '/workflow-approvals', icon: ShieldCheck, visibleTo: 'admin_and_ops' },
      { name: 'Workflow Requests', href: '/workflow-requests', icon: MessageSquare, visibleTo: 'admin_and_ops' },
      { name: 'Observability', href: '/workflow-observability', icon: BarChart3, visibleTo: 'admin_and_ops' },
      { name: 'API Keys', href: '/api-keys', icon: Key, visibleTo: 'admin_and_ops' },
      { name: 'Developer Requests', href: '/workflows/request', icon: Send, visibleTo: 'all' },
    ]
  },
  {
    name: 'Incidents',
    icon: Siren,
    productKey: 'incident_response',
    legacyProduct: true,
    children: [
      { name: 'Kubernetes Incidents', href: '/incidents', icon: ShipWheel },
      { name: 'Cloud Incidents', href: '/cloud-incidents', icon: Cloud },
      { name: 'Topology Map', href: '/topology-map', icon: Map },
      { name: 'Inventory', href: '/inventory', icon: Package },
    ]
  },
  {
    name: 'Cloud AI Copilot',
    icon: Sparkles,
    productKey: 'copilot',
    legacyProduct: true,
    children: [
      { name: 'Chat', href: '/cloud-ai-copilot', icon: BotMessageSquare },
      { name: 'Changelog', href: '/chat-changelog', icon: FileText },
    ]
  },
  { name: 'Integrations', href: '/integrations', icon: Plug, visibleTo: 'admin_and_ops' },
  { name: 'Org Management', href: '/team-management', icon: Users, visibleTo: 'admin_only' },
  { name: 'Billing', href: '/billing', icon: CreditCard, visibleTo: 'admin_only', hideOnPrem: true },
];

interface SidebarProps {
  isDrawer?: boolean;
  onNavigate?: () => void;
  /** Desktop icons-only rail. Ignored for the mobile drawer. */
  collapsed?: boolean;
  onToggleCollapse?: () => void;
}

export function Sidebar({ isDrawer = false, onNavigate, collapsed = false, onToggleCollapse }: SidebarProps = {}) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { theme } = useTheme();
  const { isProductBilling, access } = useSubscription();
  const [organizationName, setOrganizationName] = useState<string>('');
  const [expandedSection, setExpandedSection] = useState<string | null>(null);
  const [isDarkMode, setIsDarkMode] = useState<boolean>(false);
  const [isOnPremMode, setIsOnPremMode] = useState<boolean>(false);
  const [pageAccess, setPageAccess] = useState<string[] | null>(null);
  const [creatingSlackChannel, setCreatingSlackChannel] = useState<boolean>(false);
  // True while the sidebar is only open because a rail (collapsed) section
  // icon was clicked; selecting a page then re-collapses it automatically.
  const [autoExpandedFromRail, setAutoExpandedFromRail] = useState<boolean>(false);
  const [slackResult, setSlackResult] = useState<SlackConnectResult | null>(null);

  const handleCreateSlackChannel = async () => {
    if (creatingSlackChannel) return;
    setCreatingSlackChannel(true);
    try {
      const res = await apiClient.createFounderSlackChannel();
      setSlackResult(res);
    } catch (error: any) {
      const status = error?.response?.status;
      if (status === 503) {
        toast.error('Slack Connect isn\'t available right now. Please try again later.', { duration: 8000 });
      } else {
        toast.error('Couldn\'t set up your Slack channel. Please try again or email support@usekestrel.ai.', { duration: 8000 });
      }
      console.error('Failed to create founder Slack channel:', error);
    } finally {
      setCreatingSlackChannel(false);
    }
  };

  // Update dark mode state when theme changes or system preference changes
  useEffect(() => {
    const updateDarkMode = () => {
      if (theme === 'dark') {
        setIsDarkMode(true);
      } else if (theme === 'light') {
        setIsDarkMode(false);
      } else {
        // For 'system' theme, check the actual system preference
        setIsDarkMode(window.matchMedia('(prefers-color-scheme: dark)').matches);
      }
    };

    updateDarkMode();

    // Listen for system theme changes when using 'system' theme
    if (theme === 'system') {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = () => updateDarkMode();
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [theme]);

  // Fetch organization/tenant information
  useEffect(() => {
    const fetchOrganizationInfo = async () => {
      try {
        const tenant = await apiClient.getTenant();
        setOrganizationName(tenant.name || '');
      } catch (error) {
        console.error('Failed to fetch tenant info:', error);
        setOrganizationName(''); // Fallback
      }
    };

    if (user) {
      fetchOrganizationInfo();
    }
  }, [user]);

  // Fetch on-prem mode status
  useEffect(() => {
    const fetchOnPremStatus = async () => {
      try {
        const health = await apiClient.getHealthStatus();
        setIsOnPremMode(health.is_onprem_mode || false);
      } catch (error) {
        console.error('Failed to fetch on-prem status:', error);
        setIsOnPremMode(false);
      }
    };

    fetchOnPremStatus();
  }, []);

  // Fetch role access config for the current user's role
  useEffect(() => {
    if (!user || user.role === 'org_admin') return;
    const fetchConfig = async () => {
      try {
        const configs = await apiClient.getRoleAccessConfig();
        const myConfig = configs[user.role];
        if (myConfig?.page_access) {
          setPageAccess(myConfig.page_access);
        }
      } catch {
        // Use defaults from sidebar visibleTo rules
      }
    };
    fetchConfig();
  }, [user]);

  // Map sidebar href to page_access keys
  const hrefToPageKey: Record<string, string> = {
    '/workflows': 'all_workflows',
    '/workflow-executions': 'executions',
    '/workflows/suggestions': 'library',
    '/workflow-approvals': 'pending_approvals',
    '/workflow-requests': 'workflow_requests',
    '/workflow-observability': 'observability',
    '/api-keys': 'api_keys',
    '/workflows/request': 'developer_requests',
  };

  // Filter navigation items based on user role, tenant flags, and on-prem mode
  const filteredNavigation = navigation.filter(item => {
    // Legacy products (Incidents, Cloud AI Copilot) are hidden unless the
    // tenant has been explicitly re-enabled by ops.
    if (item.legacyProduct && !user?.legacy_products_enabled) {
      return false;
    }
    // Hide items marked as hideOnPrem when in on-prem mode
    if (item.hideOnPrem && isOnPremMode) {
      return false;
    }
    // If dynamic page access is loaded, it takes precedence for workflow pages
    if (pageAccess && item.href && hrefToPageKey[item.href]) {
      return pageAccess.includes(hrefToPageKey[item.href]);
    }
    if (item.visibleTo === 'admin_only' && user?.role !== 'org_admin') {
      return false;
    }
    if (item.visibleTo === 'admin_and_ops' && !isAdminOrOps(user?.role)) {
      return false;
    }
    return true;
  });

  const toggleSection = (sectionName: string) => {
    setExpandedSection(prev => prev === sectionName ? null : sectionName);
  };

  // Icons-only rail (desktop collapse). The mobile drawer always renders the
  // full sidebar.
  const isCollapsed = collapsed && !isDrawer;

  const collapsedTip = (label: string, child: React.ReactElement) => (
    <Tooltip key={label}>
      <TooltipTrigger asChild>{child}</TooltipTrigger>
      <TooltipContent side="right" className="text-xs">{label}</TooltipContent>
    </Tooltip>
  );

  const collapsedItemClass = (active: boolean) => cn(
    "flex items-center justify-center w-10 h-10 mx-auto rounded-lg transition-colors",
    active ? "bg-accent text-foreground" : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
  );

  const isActiveSection = (item: NavItem): boolean => {
    if (item.href && location.pathname === item.href) {
      return true;
    }
    if (item.children) {
      return item.children.some(child => location.pathname === child.href);
    }
    return false;
  };

  const renderNavItem = (item: NavItem) => {
    const isExpanded = expandedSection === item.name;
    const isActive = isActiveSection(item);

    // Check if this product is locked (only for product-billing customers)
    const isLocked = isProductBilling && item.productKey && !access[item.productKey];

    // Locked sections take the user to billing with context about the product
    // they tried to open, instead of silently doing nothing.
    const goToBillingForLocked = () => {
      navigate(`/billing?product=${item.productKey}`);
      onNavigate?.();
    };

    // Icons-only rail: every entry is an icon with a hover tooltip. Sections
    // expand the sidebar and open themselves, since children can't render in
    // the rail.
    if (isCollapsed) {
      if (item.children) {
        return collapsedTip(item.name, (
          <button
            onClick={() => {
              if (isLocked) {
                goToBillingForLocked();
              } else {
                onToggleCollapse?.();
                setExpandedSection(item.name);
                setAutoExpandedFromRail(true);
              }
            }}
            className={collapsedItemClass(isActive)}
          >
            <item.icon className="h-5 w-5" aria-hidden="true" />
          </button>
        ));
      }
      if (isLocked) {
        return collapsedTip(item.name, (
          <button onClick={goToBillingForLocked} className={collapsedItemClass(false)}>
            <item.icon className="h-5 w-5" aria-hidden="true" />
          </button>
        ));
      }
      return collapsedTip(item.name, (
        <Link to={item.href!} onClick={onNavigate} className={collapsedItemClass(isActive)}>
          <item.icon className="h-5 w-5" aria-hidden="true" />
        </Link>
      ));
    }

    if (item.children) {
      const showParentHighlight = isActive && !isExpanded;
      // Render parent with children
      return (
        <div key={item.name}>
          <button
            onClick={() => (isLocked ? goToBillingForLocked() : toggleSection(item.name))}
            className={cn(
              "group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg",
              isLocked
                ? "text-muted-foreground cursor-pointer hover:bg-accent"
                : showParentHighlight
                  ? "bg-accent text-foreground" 
                  : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            )}
          >
            <div className="flex items-center">
              <item.icon
                className={cn(
                  "mr-3 h-5 w-5 flex-shrink-0",
                  isLocked
                    ? "text-muted-foreground"
                    : showParentHighlight ? "text-foreground" : "text-muted-foreground group-hover:text-accent-foreground"
                )}
                aria-hidden="true"
              />
              {item.name}
              {item.badge && !isLocked && (
                <span className="ml-2 text-[10px] font-semibold leading-none px-1.5 py-0.5 rounded bg-primary text-primary-foreground">{item.badge}</span>
              )}
            </div>
            {isLocked ? (
              <Lock className="h-4 w-4 text-muted-foreground/50" />
            ) : isExpanded ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
          </button>
          
          {isExpanded && !isLocked && (
            <div className="ml-6 mt-1 space-y-1">
              {item.children
                .filter((child) => {
                  if (child.onPremOnly && !user?.is_onprem_enabled) return false;
                  // If dynamic page access is loaded, use it for workflow pages
                  if (pageAccess && child.href && hrefToPageKey[child.href]) {
                    return pageAccess.includes(hrefToPageKey[child.href]);
                  }
                  // Otherwise fall back to hardcoded role rules
                  if (child.visibleTo === 'admin_only' && user?.role !== 'org_admin') return false;
                  if (child.visibleTo === 'admin_and_ops' && !isAdminOrOps(user?.role)) return false;
                  return true;
                })
                .map((child) => {
                const isChildActive = location.pathname === child.href;
                const IconComponent = child.customIcon 
                  ? (isDarkMode ? child.customIcon.dark : child.customIcon.light)
                  : child.icon;
                const imgIconSrc = child.imgIcon ? (isDarkMode ? child.imgIcon.dark : child.imgIcon.light) : null;
                
                return (
                  <Link
                    key={child.name}
                    to={child.href}
                    onClick={() => {
                      onNavigate?.();
                      // If the sidebar only opened because the user clicked
                      // this section's rail icon, selecting a page closes it
                      // back to the rail.
                      if (autoExpandedFromRail) {
                        setAutoExpandedFromRail(false);
                        onToggleCollapse?.();
                      }
                    }}
                    className={cn(
                      "group flex items-center px-3 py-2 text-sm rounded-lg transition-colors duration-150 ease-in-out whitespace-nowrap",
                      isChildActive
                        ? "bg-accent text-foreground font-medium"
                        : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                    )}
                  >
                    {imgIconSrc ? (
                      <img
                        src={imgIconSrc}
                        alt={child.name}
                        className={cn(
                          "mr-3 h-4 w-4 flex-shrink-0 grayscale opacity-60 group-hover:opacity-100 dark:brightness-150",
                          child.imgIconClass
                        )}
                      />
                    ) : (
                    <IconComponent
                      className={cn(
                        "mr-3 h-4 w-4 flex-shrink-0",
                        isChildActive ? "text-foreground" : "text-muted-foreground group-hover:text-accent-foreground",
                        child.iconClass
                      )}
                      aria-hidden="true"
                    />
                    )}
                    {child.name}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      );
    } else {
      // Locked single entries route to billing with product context, mirroring
      // the locked-section behavior.
      if (isLocked) {
        return (
          <button
            key={item.name}
            onClick={goToBillingForLocked}
            className="group flex items-center justify-between w-full px-3 py-2 text-sm font-medium rounded-lg text-muted-foreground cursor-pointer hover:bg-accent"
          >
            <div className="flex items-center">
              <item.icon className="mr-3 h-5 w-5 flex-shrink-0 text-muted-foreground" aria-hidden="true" />
              {item.name}
            </div>
            <Lock className="h-4 w-4 text-muted-foreground/50" />
          </button>
        );
      }
      // Render simple nav item
      return (
        <Link
          key={item.name}
          to={item.href!}
          onClick={onNavigate}
          className={cn(
            "group flex items-center px-3 py-2 text-sm font-medium rounded-lg transition-colors duration-150 ease-in-out",
            isActive
              ? "bg-accent text-foreground"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          )}
        >
          <item.icon
            className={cn(
              "mr-3 h-5 w-5 flex-shrink-0",
              isActive ? "text-foreground" : "text-muted-foreground group-hover:text-accent-foreground"
            )}
            aria-hidden="true"
          />
          {item.name}
          {item.badge && (
            <span className="ml-2 text-[10px] font-semibold leading-none px-1.5 py-0.5 rounded bg-primary text-primary-foreground">{item.badge}</span>
          )}
        </Link>
      );
    }
  };

  return (
    <TooltipProvider delayDuration={100}>
    <div className={cn(
      "bg-card border-r border-border",
      isDrawer ? "h-full w-full" : cn("fixed inset-y-0 left-0 z-30 transition-[width] duration-[288ms] ease-in-out", isCollapsed ? "w-16" : "w-64")
    )}>
      <div className="flex flex-col h-full">
        {/* Logo/Title */}
        {isCollapsed ? (
          <div className="px-2 py-4 border-b border-border flex flex-col items-center gap-2">
            <img
              src={isDarkMode ? '/Logo_Kestrel AI_dark_mode.svg' : '/Logo_Kestrel AI_light_mode.svg'}
              alt="Kestrel AI"
              className="h-5 w-auto object-contain"
            />
            {onToggleCollapse && collapsedTip('Expand sidebar', (
              <button onClick={() => { setAutoExpandedFromRail(false); onToggleCollapse(); }} className={collapsedItemClass(false)}>
                <PanelLeftOpen className="h-5 w-5" aria-hidden="true" />
              </button>
            ))}
          </div>
        ) : (
          <div className="px-4 py-4 border-b border-border flex items-center justify-between">
            <img 
              src={isDarkMode ? '/Logo_Kestrel AI_full_dark_mode.svg' : '/Logo_Kestrel AI_full_light_mode.svg'}
              alt="Kestrel AI"
              className="h-5 w-auto"
            />
            {!isDrawer && onToggleCollapse && (
              <button
                onClick={() => { setAutoExpandedFromRail(false); onToggleCollapse(); }}
                className="p-1.5 rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
                title="Collapse sidebar"
              >
                <PanelLeftClose className="h-5 w-5" aria-hidden="true" />
              </button>
            )}
          </div>
        )}

        {/* Navigation */}
        <ScrollArea className={cn("flex-1 py-4", isCollapsed ? "px-2" : "px-4")}>
          <div className="space-y-1">
            {filteredNavigation.map(renderNavItem)}
          </div>
        </ScrollArea>

        {/* User section */}
        {isCollapsed ? (
          <div className="px-2 py-4 border-t border-border flex flex-col items-center gap-1">
            {collapsedTip('Docs', (
              <a
                href="https://docs.usekestrel.ai/introduction"
                target="_blank"
                rel="noopener noreferrer"
                className={collapsedItemClass(false)}
              >
                <ExternalLink className="h-5 w-5" aria-hidden="true" />
              </a>
            ))}
            {collapsedTip(creatingSlackChannel ? 'Sending invite…' : 'Connect on Slack', (
              <button
                type="button"
                onClick={handleCreateSlackChannel}
                disabled={creatingSlackChannel}
                className={cn(collapsedItemClass(false), 'disabled:opacity-60 disabled:cursor-not-allowed')}
              >
                {React.createElement(SlackIconDark, { className: 'h-5 w-5 flex-shrink-0' })}
              </button>
            ))}
            {collapsedTip('Logout', (
              <button onClick={logout} className={collapsedItemClass(false)}>
                <LogOut className="h-5 w-5" aria-hidden="true" />
              </button>
            ))}
            {collapsedTip('Settings', (
              <Link to="/settings" onClick={() => onNavigate?.()} className={collapsedItemClass(location.pathname === '/settings')}>
                <Settings className="h-5 w-5" aria-hidden="true" />
              </Link>
            ))}
          </div>
        ) : (
        <div className="px-4 py-4 border-t border-border">
          <div className="px-3 py-2 mb-2">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-foreground break-words">
                {user?.email || 'Unknown User'}
              </p>
              <p className="text-xs text-muted-foreground">
                {user?.role === 'ops_admin' ? 'Platform Admin' : user?.role ? user.role.split('_').map(word => 
                  word.charAt(0).toUpperCase() + word.slice(1)
                ).join(' ') : 'User'}
              </p>
              {organizationName && (
                <p className="text-xs text-muted-foreground truncate mt-0.5">
                  {organizationName}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center mb-1">
            <a
              href="https://docs.usekestrel.ai/introduction"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 flex items-center justify-start text-muted-foreground hover:text-accent-foreground hover:bg-accent rounded-md px-4 py-2 text-sm font-medium transition-colors"
            >
              <ExternalLink className="mr-3 h-5 w-5" />
              Docs
            </a>
          </div>
          <div className="flex items-center mb-1">
            <button
              type="button"
              onClick={handleCreateSlackChannel}
              disabled={creatingSlackChannel}
              className="group flex-1 flex items-center justify-start text-muted-foreground hover:text-accent-foreground hover:bg-accent rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {React.createElement(SlackIconDark, { className: 'mr-3 h-5 w-5 flex-shrink-0 text-muted-foreground group-hover:text-accent-foreground' })}
              {creatingSlackChannel ? 'Sending invite…' : 'Connect on Slack'}
            </button>
          </div>
          <div className="flex items-center">
            <Button
              onClick={logout}
              variant="ghost"
              className="flex-1 justify-start text-muted-foreground hover:text-accent-foreground"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </Button>
            <Link
              to="/settings"
              onClick={() => onNavigate?.()}
              className={cn(
                "p-2 rounded-md hover:bg-accent transition-colors flex-shrink-0",
                location.pathname === '/settings' ? "bg-accent text-accent-foreground" : "text-muted-foreground"
              )}
              title="Settings"
            >
              <Settings className="h-5 w-5" />
            </Link>
          </div>
        </div>
        )}
      </div>
    </div>
    <SlackConnectResultModal
      open={!!slackResult}
      onClose={() => setSlackResult(null)}
      result={slackResult}
      email={user?.email}
    />
    </TooltipProvider>
  );
} 
